import React, { useEffect, useCallback, useMemo, useState } from "react";
import { rrulestr } from "rrule";

import {
  IFlight,
  IGroundEvent,
  INewFlight,
  INewGroundEvent,
  IFlightTemplate,
  BulkActionType,
  IBulkEventPayload,
  IBulkOpsState,
  IBulkIdsMap,
  IBulkFailedEvents,
  INewFlightTemplate,
  IPairingPositionCounts,
  isFlight,
  IPlannedCreditMap,
} from "@vmosolutions/oc-interfaces";

// Redux Imports
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { retrieveFlightData, bulkEvent } from "src/redux/actions/flights/FlightData.Actions";
import { ITimeIntervalLoaded } from "src/redux/reducers/FlightTimelineReducer";
import { updateAirportOptions } from "src/utils/airportHelper";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Tooltip,
  ClickAwayListener,
  Popover,
  IconButton,
  ButtonGroup,
  Button,
  styled,
  alpha,
  Box,
} from "@mui/material";
import classNames from "classnames";
import { LoadingMessage } from "src/components/LoadingMessage/LoadingMessage";
// Flights
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { checkFlights, checkGroundEvent, checkRecurringFlights } from "./CheckConflicts";
import * as _ from "lodash";
import moment from "moment-timezone";
// Status rows.
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import HelpIcon from "@mui/icons-material/Help";

export interface IBuildPayload {
  eventPayload?: INewGroundEvent[] | INewFlight[];
  templateFile?: INewFlightTemplate;
  // Pairing Position Counts are passed in from templateFile when using recurring flights.
  pairingPositionCounts?: IPairingPositionCounts;
  includeSplits?: boolean;
  isBiddable?: boolean;
  plannedCredit?: number;
  plannedCreditMap?: IPlannedCreditMap;
}

interface IBuildStatus {
  mosaicId: string;
  eventType: { value: string; label: string };
  payload: IBuildPayload;
  // setFocusOnEvent?: (event: IFlight | IGroundEvent) => void;
  setUpdateTableEvents?: (updateTableEvents: IUpdateTableEvents) => void;
  className?: string;
  // optional template recurrence start/end if passed by parent.
  parentRuleStart?: number;
  parentRuleUntil?: number;
  startDate?: number;
  setStartDate?: React.Dispatch<React.SetStateAction<number>>;
  buildAsLocal?: boolean;
}

export interface IEventStatus extends Partial<INewFlight>, Partial<INewGroundEvent> {
  _id?: string;
  tempId?: string; // preserve temp id so that item can be accessed and modified in pending table.
  recurrenceId?: string; // preserve recurrence id.
  status?: "pending" | "conflict" | "success" | "failed";
  statusMessage?: string;
  conflictEvent?: IGroundEvent | IFlight; // if a conflicting event is found, store it here.
  dayOffset?: number;
}

export interface IPairingEventStatus {
  _id?: string;
  tempId?: string; // preserve temp id so that item can be accessed and modified in pending table.
  recurrenceId?: string; // preserve recurrence id.
  status?: "pending" | "conflict" | "success" | "failed";
  statusMessage?: string;
  // Pairing related info
  flights: IEventStatus[];
  pairingPositionCounts?: IPairingPositionCounts;
  includeSplits?: boolean;
  isBiddable?: boolean;
  plannedCredit?: number;
  plannedCreditMap?: IPlannedCreditMap;
  shortId?: string;
  start?: number;
  end?: number;
  // conflictEvent?: IGroundEvent | IFlight; // if a conflicting event is found, store it here.
}

export interface IUpdateTableEvents {
  cleanOut?: boolean;
  resetAll?: boolean;
  // All successfully flights listed in object with tempId: _id
  [key: string]: any;
}

const OCBuildStatus = styled(Card)(({ theme }) => ({
  display: "flex",
  width: 850,
  flexGrow: 1,
  flexDirection: "column",
  backgroundColor: theme.custom.tables.backgroundColor,
  margin: "5px 25px 5px 5px",
  padding: 10,
  position: "absolute",
  zIndex: 2,
  overflow: "auto",
  boxShadow: theme.shadows[5],
  "& .build_status_header": {
    padding: 0,
  },
  "& .button_container": { display: "flex" },
  "& .build_status_content": {
    margin: 0,
    padding: 0,
    "& .overlapping-flight": {
      display: "flex",
      alignContent: "center",
      "& .error_string": {
        color: theme.palette.error.main,
        margin: "0px 10px",
        fontSize: 12,
        lineHeight: "16px",
      },
    },
    "& .recurrence_timeframe": {
      display: "flex",
    },
  },
}));

const OCArrowButtons = styled(Button)(({ theme }) => ({
  margin: "6px 8px",
  "&.build_btn": { margin: 0 },
  "&.MuiButton-text": { padding: 0 },
  "& .button_icon": {
    width: 32,
    height: 32,
    "& path": { fill: theme.palette.primary.main },
  },
  "& .button_label": {
    display: "flex",
    height: 32,
    alignItems: "center",
    padding: "0px 10px",
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderRadius: "0px 3px 3px 0px",
    lineHeight: "14px",
    "& .warning": {
      margin: "0px 5px",
      fontSize: 12,
      fontWeight: 300,
      textTransform: "lowercase",
    },
  },
  // conditional style when icon is placed on the right
  "&.icon_right": {
    "& .button_label": { borderRadius: "3px 0px 0px 3px" },
  },
  "&.flip_icon": {
    "& .button_icon": { transform: "scaleX(-1)" },
  },
  "&.no_icon": {
    "& .button_label": { borderRadius: "3px 3px 3px 3px" },
  },
  "&.Mui-disabled": { opacity: 0.5 },
  // Disable standard button effects and replace with our own.
  "& .MuiTouchRipple-root": { display: "none !important" },
  "&:hover": {
    backgroundColor: "transparent !important",
    "& .button_icon": {
      "& path": {
        fill: theme.palette.primary.dark,
      },
    },
    "& .button_label": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const OCEventBuilds = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  overflow: "none",
  height: 16,
  "& .flight_builder_table": { width: "100%" },
  "&.expanded": { height: "auto" },
  "& .group": {
    marginTop: 2,
    display: "flex",
    padding: 2,
    border: `1px solid ${theme.custom.forms.inputBorder}`,
    width: "100%",
  },
  "& .pending_events": {
    display: "flex",
  },
  "& .event_status": {
    display: "flex",
    alignItems: "center",

    "& .status_message": {
      display: "flex",
      alignItems: "center",
      padding: "2px 5px",
      maxWidth: 300,
      fontSize: 12,
      lineHeight: "12px",
      fontWeight: 300,
      color: theme.palette.primary.contrastText,
      "& .flightchip, & .eventchip": {
        margin: "0px 6px ",
        "&:hover": { width: 120 },
      },
      "& .status_arrow": {
        color: alpha(theme.palette.error.contrastText, 0.1),
      },
      "& .conflict_icon": {
        margin: "0px 5px",
      },
      "& .status_icon": {
        display: "flex",
        "& button": {
          backgroundColor: alpha(theme.palette.error.contrastText, 0.1),
          borderRadius: 3,
          padding: 0,
          margin: "0px 5px",
          width: 24,
          height: 24,
          overflow: "hidden",
        },

        "& .icon_foreground": {
          width: "100%",
          marginTop: 1,
          // Active State - blue
          "&.pending": {
            marginTop: 3,
            "& path": {
              fill: `${theme.palette.primary.main} !important`,
            },
          },
          // Conflict State - orange
          "&.conflict > path": {
            fill: `${theme.palette.secondary.main} !important`,
          },
          // Error State - red
          "&.failed > path": {
            fill: `${theme.palette.error.main} !important`,
          },
          // Success State - green
          "&.success": {
            marginTop: 3,
            "& path, & polygon": {
              fill: `${theme.palette.success.main} !important`,
            },
          },
        },
      },
    },
  },
}));

/**
 * return a default date range of the start of cur day and the end of the next day
 * @returns epoch values for start/end times
 */
export const defaultDateRange = (): number[] => {
  const today = new Date();
  const startEpoch = moment.utc(today).startOf("day")?.valueOf();
  const endEpoch = moment.utc(today).endOf("day")?.valueOf();
  return [startEpoch, endEpoch];
};

// Returns build status on pending flights in the bulk batch queue.
const setBuildState = (
  eventsPayload: IEventStatus[] | IPairingEventStatus[],
  successfulEventIds: IBulkIdsMap[],
  failedEvents: IBulkFailedEvents[] | null
): IEventStatus[] => {
  const events = _.cloneDeep(eventsPayload); // Ensured that incoming data isn't mutated
  events.forEach((event: IEventStatus | IPairingEventStatus) => {
    const successfulEvent = successfulEventIds.find((build: IBulkIdsMap) => build.tempId === event._id);
    const failedEvent = failedEvents?.find((failedEvent: IBulkFailedEvents) => failedEvent.tempId === event._id);
    const isPairing = (event as IPairingEventStatus)?.flights?.length;

    // If even update wasn't found, irecursively call function to update nested flights.
    if (!successfulEvent && !failedEvent && (event as IPairingEventStatus)?.flights) {
      const updatedFlights: IEventStatus[] = setBuildState(
        (event as IPairingEventStatus)?.flights,
        successfulEventIds,
        failedEvents
      );
      (event as IPairingEventStatus).flights = updatedFlights;
    }

    if (successfulEvent) {
      if (event.recurrenceId) {
        event.tempId = event.recurrenceId; // If recurring flight, set temp as recurring.
      } else {
        event.tempId = event._id; // set pending _id as tempId to preserve it.
      }
      if (isPairing) {
        (event as IPairingEventStatus).shortId = successfulEvent.shortId;
      } else if (isFlight(event)) {
        //set the actual times so that drag and drop of flight works/flight chip has the info
        event.actualOut = event.actualOut ?? event.scheduledOut;
        event.actualIn = event.actualIn ?? event.scheduledIn;
      }
      event._id = successfulEvent.objectId;
      event.status = "success";
      event.statusMessage = "Build Successful";
    } else if (failedEvent) {
      //find the error message
      const failedBuild: IBulkFailedEvents = failedEvents?.find(
        (failedEvent: IBulkFailedEvents) => failedEvent.tempId === event._id
      );
      event.status = "failed";
      event.statusMessage = `Build Failed: ${failedBuild?.errorMessage}`;
    }
    return event;
  });
  return events;
};

// Strip out unwanted keys from the events before passing to builder.
const preparePayload = (eventsToBuild: IEventStatus[], isFlight: boolean): INewFlight[] | INewGroundEvent[] => {
  const newEventPayload = eventsToBuild.map((event: IEventStatus) => {
    const { tempId, recurrenceId, status, statusMessage, conflictEvent, ...newEvent } = event;
    const minimizedEvent = isFlight
      ? {
          ...newEvent,
          aircraft: event.aircraft?._id ? { _id: event.aircraft?._id } : null,
          aircraftConfiguration: event.aircraftConfiguration?._id ? { _id: event.aircraftConfiguration?._id } : null,
          customer: event.customer?._id ? { _id: event.customer?._id } : null,
          flightType: event.flightType?._id ? { _id: event.flightType?._id } : null,
        }
      : {
          ...newEvent,
          aircraft: event.aircraft?._id ? { _id: event.aircraft?._id } : null,
          eventType: event.eventType?._id ? { _id: event.eventType?._id } : null,
        };
    return minimizedEvent;
  });
  return newEventPayload as INewFlight[] | INewGroundEvent[];
};

// Default start and until times.
const [defaultStart, defaultEnd] = defaultDateRange();

const eventStatusRowInfo = [
  {
    id: "statusRowId",
    value: "statusRowId",
    index: 1,
    numeric: false,
    label: "Temp Id",
    type: "string",
    width: 10,
    visible: false,
    filter: false,
  },
  {
    id: "startDate",
    value: "startDate",
    index: 2,
    numeric: false,
    label: "Start/End",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "pendingEvent",
    value: "pendingEvent",
    index: 3,
    numeric: true,
    label: "Events Pending Creation",
    type: "object",
    width: 10,
    visible: true,
    filter: false,
  },
];

// Return Pending Event table data.
const pendingTableData = (eventsToBuild: any[]): any => {
  const eventsArePairings =
    eventsToBuild.length > 0 &&
    eventsToBuild[0]?.hasOwnProperty("flights") &&
    eventsToBuild[0]?.hasOwnProperty("pairingPositionCounts");

  const tableRows = eventsToBuild?.map((event) => ({
    statusRowId: eventsArePairings ? `pairing_${event?._id}` : `flight_${event?._id}`,
    pendingEvent: event,
    startDate: eventsArePairings ? event.start : (event.scheduledOut ?? event.scheduledStart),
  }));

  return [
    {
      id: eventsArePairings ? "PendingPairingStatus" : "PendingEventStatus",
      dataType: eventsArePairings ? "PendingPairingStatus" : "PendingEventStatus",
      label: "",
      tableInfoDisplay: "",
      data: tableRows?.length ? tableRows : [],
      tableInfo: eventStatusRowInfo,
    },
  ];
};

// Sort the FilterSearchTable by assigned startDate.
const defaultSort = (a: any, b: any): number => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0);

/**
 * BUILD STATUS COMPONENT - used to display status on pending event builds and potential conflicts with existing data.
 * @param props -
 */
export const BuildStatus = (props: IBuildStatus): JSX.Element => {
  const {
    mosaicId,
    eventType,
    payload,
    setUpdateTableEvents,
    className,
    parentRuleStart,
    parentRuleUntil,
    startDate,
    setStartDate,
    buildAsLocal,
  } = props;
  const dispatch = useDispatch<any>();
  const [anchorEl, setAnchorEl] = useState<Element>(null);

  const [expanded, setExpanded] = useState<boolean>(false);
  // Increment this value to force table to refresh if data changes.
  const [forceRefresh, setForceRefresh] = useState<number>(0);

  const timeIntervalLoaded: ITimeIntervalLoaded = useSelector(
    (state: IRootState) => state.timelineFlights.timeIntervalLoaded
  );
  const allAircraft = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);
  const FlightData = useSelector((state: IRootState) => state.timelineFlights.flightData);
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);

  // Build Status Values
  const [eventsToBuild, setEventsToBuild] = useState<(IEventStatus | IPairingEventStatus)[]>([]);
  const [building, setBuilding] = useState<boolean>(false);

  // If parent is sending date info, hide date selectors.
  const showDateFields = parentRuleStart || parentRuleUntil ? false : true;

  // Recurence Start and End ISO Dates.
  const [ruleUntil, setRuleUntil] = useState<number>(defaultEnd);
  // if using local times, store local offset (in minutes) here so it can be applied to rule start and until.
  const [ruleOffset, setRuleOffset] = useState<number>(0);

  // For BULK batch tracking
  const [batchId, setBatchId] = useState<any>(null);
  const batchJob: IBulkOpsState = useSelector((state: IRootState) => state.bulkBatch[batchId]);
  const eventsBuilt = batchJob ? batchJob?.successfulEventIds?.length : 0;
  const eventsFailed = batchJob ? batchJob?.failedEvents?.length : 0;
  const buildsAttempted: number = eventsBuilt + eventsFailed;

  // Load default values for airport data so they display in the selection menus.
  const loadAirportData = useCallback(
    (icao: string) => {
      updateAirportOptions(icao, airportOptions, dispatch);
    },
    [airportOptions, dispatch]
  );

  // Retrieve initial AirportOptions for location if not in airport options.
  const getAirportTimezone = useCallback(
    (locationICAO: string) => {
      if (locationICAO && !airportOptions[locationICAO]) {
        loadAirportData(locationICAO);
        return "";
      }

      if (!!locationICAO && !!airportOptions[locationICAO]) {
        return airportOptions[locationICAO].timezoneId;
      }
      return "";
    },
    [airportOptions, loadAirportData]
  );

  /**
   * Monitor events successfully built and pass array of temp ids to parent.
   */
  // const [cleanOut, setCleanOut] = useState<boolean>(false);
  useEffect(() => {
    if (eventsToBuild?.length === 0) {
      return;
    }
    const updateTableEvents: IUpdateTableEvents = {
      cleanOut: false, // disable cleanout so that users can see flight chips in creation table.
    };
    eventsToBuild.forEach((item) => {
      // If item is a flight, check to see if it's flights have built.
      const { status, _id, tempId } = item;
      if (status === "success" && _id && tempId) {
        updateTableEvents[tempId] = _id;
      }
      // If item is a pairing, check to see if it's flights have built.
      if (item.hasOwnProperty("flights")) {
        (item as IPairingEventStatus).flights.forEach((pairing) => {
          const { status, _id, tempId } = pairing;
          if (status === "success" && _id && tempId) {
            updateTableEvents[tempId] = _id;
          }
        });
      }
    });
    // DO not remove recurring flight patterns when created.
    if (setUpdateTableEvents && eventType?.value !== "RecurrentFlight") {
      setUpdateTableEvents(updateTableEvents);
    }
  }, [eventsToBuild, setUpdateTableEvents, eventType]);

  // Load Flight Data (to check for duplicates in the build queue) if not already
  // this could be improved on, by calling retrieveFlightData with the interval of
  // the first leg date, last leg date
  useEffect(() => {
    // This `!timeIntervalLoaded` always return false because the defult value is `{}`
    //so we never go into this case
    if (!timeIntervalLoaded && allAircraft.length > 0) {
      // For now use the same dates that the flight timeline uses as the the flightDataLoaded variables is set to true once it's loaded.
      // Eventually we'll replace this with live loading similar to the crew section.
      const currentDate = new Date();
      const startCheckDate = new Date(new Date().setDate(currentDate.getUTCDate() - 45));
      const endCheckDate = new Date(new Date().setDate(currentDate.getUTCDate() + 45));
      dispatch(retrieveFlightData(startCheckDate, endCheckDate));
    }
  }, [allAircraft.length, dispatch, timeIntervalLoaded]);

  // first origin is used to determine the localization of the start/until recurring patterns
  const ruleTimezone = useMemo(() => {
    const location = payload?.templateFile?.legs?.length
      ? payload?.templateFile?.legs?.[0]?.origin
      : ((payload?.eventPayload?.[0] as IFlight)?.origin ?? (payload?.eventPayload?.[0] as INewGroundEvent)?.location);
    const locationTZ = location && buildAsLocal ? getAirportTimezone(location) : "Etc/UTC";
    const offset = moment.utc(startDate)?.tz(locationTZ)?.utcOffset();
    setRuleOffset(-offset);
    return locationTZ;
  }, [getAirportTimezone, payload, startDate, buildAsLocal]);

  const showHelpBubble = useCallback((event: any): void => {
    event.persist();
    setAnchorEl(event.currentTarget);
  }, []);

  // INITIALIZE Build action.
  const handleBuildFlights = useCallback(async () => {
    if (eventsToBuild?.length === 0) {
      return;
    }

    setBuilding(true);
    // If the events to build have a flight key, build bulk pairings
    const buildPairings = (eventsToBuild[0] as IPairingEventStatus)?.hasOwnProperty("flights");

    const bulkActionType =
      eventType.value === "GroundEvent"
        ? BulkActionType.CREATE_GROUND_EVENT
        : (eventType.value === "Flight" || eventType.value === "RecurrentFlight") && !buildPairings
          ? BulkActionType.CREATE_FLIGHT
          : BulkActionType.CREATE_FLIGHT_FOR_PAIRING;

    // PairingPayload is null if no pairing is required.
    const pairingPayload = buildPairings
      ? (eventsToBuild as IPairingEventStatus[]).map((pairingEvent) => {
          const minimizedFlights = pairingEvent.flights.map((flight: any) => {
            const { tempId, recurrenceId, status, statusMessage, conflictEvent, ...flt } = flight;
            return {
              ...flt,
              aircraft: flt.aircraft?._id ? { _id: flt.aircraft?._id } : null,
              aircraftConfiguration: flt.aircraftConfiguration?._id ? { _id: flt.aircraftConfiguration?._id } : null,
              customer: flt.customer?._id ? { _id: flt.customer?._id } : null,
              flightType: flt.flightType?._id ? { _id: flt.flightType?._id } : null,
            };
          });
          return {
            flights: minimizedFlights,
            pairingId: pairingEvent._id,
            positionsCount: pairingEvent.pairingPositionCounts,
            includeSplits: pairingEvent.includeSplits,
            isBiddable: pairingEvent.isBiddable,
            plannedCredit: pairingEvent.plannedCredit,
            plannedCreditMap: pairingEvent.plannedCreditMap,
          };
        })
      : null;

    const bulkPayload: IBulkEventPayload = {
      actionType: bulkActionType,
      batchIds: [],
      payload:
        pairingPayload ?? preparePayload(eventsToBuild, bulkActionType === BulkActionType.CREATE_FLIGHT ? true : false),
    };

    const newBatchId = await dispatch(bulkEvent(bulkPayload, eventsToBuild?.length));
    setBatchId(newBatchId);
  }, [eventsToBuild, eventType.value, dispatch]);

  // If event types change, increment force refresh integer to redraw table
  const eventsArePairings =
    eventsToBuild.length > 0 &&
    eventsToBuild[0]?.hasOwnProperty("flights") &&
    eventsToBuild[0]?.hasOwnProperty("pairingPositionCounts");
  useEffect(() => setForceRefresh((prev) => prev + 1), [eventsArePairings]);

  // PAYLOAD IS LOADED IN...
  // Caculate Build Counts and determine conclicts with existing flight data.
  const setInitialState = useCallback(() => {
    setBatchId(null);
    setBuilding(false);
    let pendingEventsToBuild: IEventStatus[] = [];

    switch (eventType.value) {
      case "RecurrentFlight": {
        /////////////// RECURRING FLIGHTS
        const { templateFile } = payload;

        const useStart = parentRuleStart ?? startDate;
        const useUntil = parentRuleUntil ?? ruleUntil;

        // BUILD FOLDER
        if (Array.isArray(templateFile)) {
          templateFile.forEach((file: IFlightTemplate) => {
            if (file?.rRule && file?.legs?.length > 0 && useStart && useUntil) {
              const RRule = rrulestr(file.rRule);
              if (RRule?.options) {
                RRule.options.dtstart = moment(useStart).add(ruleOffset, "minutes").toDate();
                RRule.options.until = moment(useUntil).add(ruleOffset, "minutes").toDate();
                const oneFilesEventsToBuild = checkRecurringFlights(
                  RRule,
                  file.legs,
                  FlightData,
                  file?.pairingPositionCounts,
                  file?.includeSplits,
                  file?.isBiddable,
                  file?.asLocal,
                  //@ts-ignore
                  file?.plannedCredit,
                  file?.plannedCreditMap,
                  getAirportTimezone
                );
                pendingEventsToBuild = [...pendingEventsToBuild, ...oneFilesEventsToBuild];
              }
            }
          });
        } else {
          // BUILD TEMPLATE
          if (templateFile?.rRule && templateFile?.legs?.length > 0 && useStart && useUntil) {
            const RRule = rrulestr(templateFile.rRule);
            if (RRule?.options) {
              RRule.options.dtstart = moment(useStart).add(ruleOffset, "minutes").toDate();
              RRule.options.until = moment(useUntil).add(ruleOffset, "minutes").toDate();
              pendingEventsToBuild = checkRecurringFlights(
                RRule,
                templateFile.legs,
                FlightData,
                templateFile?.pairingPositionCounts,
                templateFile?.includeSplits,
                templateFile?.isBiddable,
                templateFile?.asLocal,
                //@ts-ignore
                templateFile?.plannedCredit,
                templateFile?.plannedCreditMap,
                getAirportTimezone
              );
            }
          }
        }
        break;
      }
      case "Flight": /////////////// FLIGHT(s)
      case "GroundEvent": {
        ////////// GROUND EVENT(s)
        const { eventPayload, pairingPositionCounts, includeSplits, isBiddable, plannedCredit, plannedCreditMap } =
          payload;
        if (eventPayload?.length > 0) {
          const eventsToBuild: (IEventStatus | IPairingEventStatus)[] =
            eventType.value === "GroundEvent"
              ? checkGroundEvent(eventPayload, FlightData)
              : checkFlights(
                  moment(startDate).valueOf(),
                  buildAsLocal,
                  getAirportTimezone,
                  eventPayload,
                  FlightData,
                  pairingPositionCounts,
                  includeSplits,
                  isBiddable,
                  plannedCredit,
                  plannedCreditMap
                );
          pendingEventsToBuild = eventsToBuild;
        }
        break;
      }
      default:
        break;
    }

    // Update events to build.
    setEventsToBuild(pendingEventsToBuild);
  }, [
    eventType.value,
    payload,
    startDate,
    buildAsLocal,
    ruleUntil,
    FlightData,
    getAirportTimezone,
    parentRuleStart,
    parentRuleUntil,
    ruleOffset,
  ]);

  // If user switches event type, clear state.
  useEffect(() => {
    setBatchId(null);
    setBuilding(false);
    setEventsToBuild([]);
    if (setStartDate) setStartDate(defaultStart);
    setRuleUntil(defaultEnd);
  }, [eventType.value, setStartDate]);

  // Re-Initialize Status if payload loads and batchId isn't set.
  useEffect(() => {
    if (!!timeIntervalLoaded && payload && !batchId && !building) {
      setInitialState();
    }
  }, [batchId, setInitialState, payload, timeIntervalLoaded, building]);

  // WATCH BATCHJOB and update event status accordingly.
  // These are stored in state so that events not using bulk creation can use store values as well.
  useEffect(() => {
    if (batchJob) {
      const { records, successfulEventIds, failedEvents } = batchJob;
      const newEventStatus = setBuildState(eventsToBuild, successfulEventIds, failedEvents);
      if (!_.isEqual(newEventStatus, eventsToBuild)) {
        setEventsToBuild([...newEventStatus]);
      }
      // Build completed
      if (successfulEventIds?.length + failedEvents?.length === records) {
        setBuilding(false);
      }
    }
  }, [batchJob, buildsAttempted, eventsToBuild]);

  const buildLabel: string = useMemo(() => {
    const str = eventType.label;
    const singleTypeLabel = str.replace(str.substring(str.length - 1), "").toLowerCase();
    const typelabel = eventsToBuild.length < 2 ? singleTypeLabel : str.toLowerCase();
    return typelabel;
  }, [eventType, eventsToBuild]);

  return (
    <OCBuildStatus className={`${className ?? ""}`}>
      <Grid container direction="column">
        <Grid item xs={12}>
          <CardHeader className="build_status_header" title="Build Status" />
        </Grid>
        {building && (
          <Grid item xs={12}>
            <Typography className="build_status_subheader" variant="body1">
              <LoadingMessage animate={true} message="Building pending events" />
            </Typography>
          </Grid>
        )}

        <CardContent className="build_status_content">
          <Grid container direction="column" spacing={0}>
            <Grid container wrap="nowrap" alignItems="center">
              {eventType.value === "RecurrentFlight" && showDateFields && (
                <Grid item className="recurrence_timeframe">
                  <Tooltip
                    placement="top"
                    title={`${moment
                      .tz(startDate, ruleTimezone)
                      .add(ruleOffset, "minutes")
                      .format("MMM Do, Y HH:mm z")}`}
                  >
                    <Grid item>
                      <DateTimeField
                        label="Start"
                        className="start_date"
                        name="start"
                        value={startDate?.valueOf()}
                        onChange={(date) => setStartDate(moment.utc(date).startOf("day")?.valueOf())}
                        isEpoch={true}
                        hideTimeControl={true}
                        labelStyle={{ width: "25px !important" }}
                      />
                    </Grid>
                  </Tooltip>
                  <Tooltip
                    placement="top"
                    title={`${moment
                      .tz(ruleUntil, ruleTimezone)
                      .add(ruleOffset, "minutes")
                      .format("MMM Do, Y HH:mm z")}`}
                  >
                    <Grid item>
                      <DateTimeField
                        label="Until"
                        className="until_date"
                        name="until"
                        value={ruleUntil?.valueOf()}
                        onChange={(date) => setRuleUntil(moment.utc(date).endOf("day")?.valueOf())}
                        isEpoch={true}
                        meta={{
                          error: startDate > ruleUntil ? "Until date must be after start." : null,
                          touched: startDate > ruleUntil ? true : false,
                        }}
                        hideTimeControl={true}
                        labelStyle={{ width: "30px !important" }}
                      />
                    </Grid>
                  </Tooltip>
                </Grid>
              )}
              {eventType.value === "Flight" && (
                <Grid item>
                  <DateTimeField
                    label="Start"
                    className="start_date"
                    name="start"
                    value={startDate?.valueOf()}
                    onChange={(date) => setStartDate(moment.utc(date).startOf("day")?.valueOf())}
                    isEpoch={true}
                    labelStyle={{ width: "25px !important" }}
                  />
                </Grid>
              )}
              {eventType.value === "GroundEvent" && (
                <Grid item>
                  <Box sx={{ height: "22px" }}></Box>
                </Grid>
              )}
              {eventType?.value !== "GroundEvent" && (
                <Grid item>
                  <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                    <Grid container spacing={0}>
                      <Grid item>
                        <IconButton sx={{ margin: "0px", marginLeft: "4px", padding: "0px" }} onClick={showHelpBubble}>
                          <HelpIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                      <Popover
                        style={{ zIndex: 9999, marginLeft: "10px" }}
                        open={!!anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorEl={anchorEl}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        transformOrigin={{ vertical: "center", horizontal: "left" }}
                        disableRestoreFocus
                      >
                        <Grid item sx={{ padding: "10px", width: "300px" }}>
                          <Typography sx={{ fontSize: 11 }}>
                            The date range between which flight sequences begin. if using local time, this is relative
                            to the timezone of the first flight's origin.
                          </Typography>
                        </Grid>
                      </Popover>
                    </Grid>
                  </ClickAwayListener>
                </Grid>
              )}
            </Grid>

            <Grid item alignItems="center" sx={{ marginTop: "5px" }}>
              <ButtonGroup variant="contained" size="small">
                <OCArrowButtons
                  onClick={() => {
                    setInitialState();
                    if (setUpdateTableEvents) setUpdateTableEvents({ resetAll: true });
                  }}
                  className={classNames("build_btn")}
                  disabled={!batchId}
                >
                  Reset
                </OCArrowButtons>
                <Button
                  color="success"
                  onClick={handleBuildFlights}
                  disabled={eventsToBuild.length === 0 || building || !!batchId}
                >
                  Build {buildLabel} ({eventsToBuild.length})
                </Button>
                <Button disabled={eventsToBuild?.length === 0} onClick={() => setExpanded(!expanded)}>
                  {expanded ? "Hide Status Table" : "Show Status Table"}
                </Button>
              </ButtonGroup>
            </Grid>
            <OCEventBuilds className={`${expanded && "expanded"}`} style={{ marginTop: 10 }}>
              <Grid item>
                {eventsToBuild?.length > 0 && expanded && (
                  <FilterSearchTable
                    className="flight_builder_table"
                    key={forceRefresh} // incrementing value will force refresh of component.
                    id={mosaicId}
                    events={pendingTableData(eventsToBuild ?? [])}
                    uniqueKey="statusRowId"
                    hideSelectionCheckboxes={true}
                    hideFocusSelected={true}
                    defaultRowsPerPage={10}
                    hideShowColumns={true}
                    selectModeSingle={true}
                    hideFilter={true}
                    disableRowSelect={true}
                    orderDirection="asc"
                    defaultOrderKey="startDate" // Table will use this key to sort it's content.
                  />
                )}
              </Grid>
            </OCEventBuilds>
          </Grid>
        </CardContent>
      </Grid>
    </OCBuildStatus>
  );
};
